// src/utils/constants.js

/**
 * Fontana Ecosystem - Contract Addresses & Configuration
 * Network: BSC Testnet
 */

// ============================================================================
// CONTRACT ADDRESSES
// ============================================================================

export const CONTRACTS = {
  FONTANA: '0x31D378ed0C52FaA4B679e24f579dA4DAc00A24EF',
  LP_PAIR: '0x65c7C38Fad243B2D0903BdA2833a08d1BbFB6019',
  TAX: '0xD98ef6Ccbc2d57248e92C5dd6D495d8E2BCD23fa',
  FACTORY: '0xa2ceF2E3F10c6585C2e9AcbB49685209cA015B8b',
  RECOIL: '0x6Ab40E7B2e36cF0b74990447D1CC296d2c4ba77A',
  
  // PancakeSwap on BSC Testnet
  PANCAKE_ROUTER: '0xD99D1c33F9fC3444f8101754aBC46c52416550D1',
  PANCAKE_FACTORY: '0x6725F303b657a9451d8BA641348b6761A6CC7a17',
 // USDT on BSC Testnet
  USDT: '0x337610d27c682E347C9cD60BD4b3b107C9d34dDd'
};

// ============================================================================
// NETWORK CONFIGURATION
// ============================================================================

export const NETWORK = {
  chainId: '0x61', // 97 in hex
  chainName: 'BNB Smart Chain Testnet',
  rpcUrl: 'https://data-seed-prebsc-1-s1.binance.org:8545/',
  blockExplorerUrl: 'https://testnet.bscscan.com',
  nativeCurrency: {
    name: 'tBNB',
    symbol: 'tBNB',
    decimals: 18
  }
};

// ============================================================================
// TOKEN CONFIGURATION
// ============================================================================

export const TOKEN_CONFIG = {
  NAME: 'Fontana',
  SYMBOL: 'FNT',
  DECIMALS: 18,
  MAX_SUPPLY: '6000000000', // 6 billion
  BURN_LIMIT: '1000000000', // 1 billion
  CERULEAN_UNLOCK_VOLUME: '100000000000' // 100 billion
};

// ============================================================================
// TAX SYSTEM CONSTANTS
// ============================================================================

export const TAX_RATES = {
  BUY: 1,              // 1%
  SELL: 2,             // 2%
  PENALTY: 5,          // 5% (SVT/Price Impact breach)
  CEX_DEPOSIT: 25,     // 25%
  TRANSFER: 1          // 1%
};

export const TAX_DISTRIBUTION = {
  BURN: 10,            // 10% (until 1B burned)
  BONUS_POOL: 30,      // 30%
  RVP_REWARDS: 20,     // 20%
  DEVELOPMENT: 25,     // 25%
  CERULEAN_VAULT: 5,   // 5%
  RECOIL_MANAGER: 10   // 10%
};

// ============================================================================
// SVT & PRICE IMPACT THRESHOLDS
// ============================================================================

export const THRESHOLDS = {
  SVT_WINDOW: 180,              // 3 minutes (seconds)
  SVT_PENALTY_DURATION: 60,     // 1 minute (seconds)
  SVT_POOL_PERCENTAGE: 11,      // 11% of pool
  PRICE_IMPACT: 10,             // 10% (1000 basis points)
  RECOIL_THRESHOLD: '2000000',  // 2M FNT
  RECOIL_COOLDOWN: 300          // 5 minutes (seconds)
};

// ============================================================================
// DASHBOARD REFRESH INTERVALS (milliseconds)
// ============================================================================

export const REFRESH_INTERVALS = {
  FAST: 5000,      // 5 seconds - Price, LP reserves
  MEDIUM: 15000,   // 15 seconds - Volume, treasury
  SLOW: 60000,     // 1 minute - Total supply, burn status
  TRANSACTIONS: 10000  // 10 seconds - Recent tx
};

// ============================================================================
// UI CONFIGURATION
// ============================================================================

export const UI_CONFIG = {
  RECENT_TX_LIMIT: 10,
  CHART_HISTORY_POINTS: 50,
  PRICE_DECIMALS: 8,
  TOKEN_DECIMALS_DISPLAY: 2,
  PERCENTAGE_DECIMALS: 2
};

// ============================================================================
// STATUS INDICATORS
// ============================================================================

export const STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',
  BREACH: 'breach',
  READY: 'ready',
  COOLDOWN: 'cooldown',
  ERROR: 'error'
};

export const STATUS_COLORS = {
  [STATUS.ACTIVE]: '#10b981',    // Green
  [STATUS.PENDING]: '#f59e0b',   // Yellow/Orange
  [STATUS.BREACH]: '#ef4444',    // Red
  [STATUS.READY]: '#3b82f6',     // Blue
  [STATUS.COOLDOWN]: '#6b7280',  // Gray
  [STATUS.ERROR]: '#dc2626'      // Dark Red
};

// ============================================================================
// EXPLORER LINKS
// ============================================================================

export const getExplorerLink = (address, type = 'address') => {
  const base = NETWORK.BLOCK_EXPLORER;
  switch (type) {
    case 'tx':
      return `${base}/tx/${address}`;
    case 'token':
      return `${base}/token/${address}`;
    case 'address':
    default:
      return `${base}/address/${address}`;
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const shortAddress = (address, chars = 4) => {
  if (!address) return '';
  return `${address.substring(0, chars + 2)}...${address.substring(42 - chars)}`;
};

export const toWei = (amount) => {
    if (!amount) return '0';
  const num = Number(amount);
  const multiplier = Math.pow(10, TOKEN_CONFIG.DECIMALS);
  return String(Math.floor(num * multiplier));
};

export const fromWei = (amount) => {
  if (!amount) return 0;
  const divisor = Math.pow(10, TOKEN_CONFIG.DECIMALS);
  return Number(amount) / divisor;
};

// ============================================================================
// VALIDATION
// ============================================================================

export const isValidAddress = (address) => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

export const isValidTxHash = (hash) => {
  return /^0x[a-fA-F0-9]{64}$/.test(hash);
};